import React from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import dayCareImage from "assets/daycare.jpg";
import nightCareImage from "assets/nightcare.jpg";
import stayAtHomeImage from "assets/stayathome.jpg";
// You'll need to add these images to your assets folder
import doctorVisitImage from "assets/imgcare.jpg";
import counselingImage from "assets/genricimg.jpg";

const ServiceSection = ({ servicesRef }) => {
  const theme = useTheme();

  const services = [
    {
      name: "AT HOME DAY CARE",
      image: dayCareImage,
      description:
        "Professional daytime care ensuring comfort, safety, and well-being.",
      features: [
        "8–12 Hour Shifts",
        "Medication Support",
        "Daily Activities",
        "Meal Preparation",
      ],
    },
    {
      name: "AT HOME NIGHT CARE",
      image: nightCareImage,
      description:
        "Overnight supervision and assistance for peaceful, safe nights.",
      features: [
        "Overnight Monitoring",
        "Sleep Assistance",
        "Emergency Response",
        "Comfort Care",
      ],
    },
    {
      name: "STAY AT HOME CARE",
      image: stayAtHomeImage,
      description: "24/7 personalized care and companionship at your home.",
      features: [
        "24/7 Care",
        "Personal Assistance",
        "Household Help",
        "Companionship",
      ],
    },
    {
      name: "DOCTOR HOME VISIT",
      icon: "🩺",
      description:
        "Qualified doctors visit patients at home for regular check-ups, chronic condition monitoring, prescription support, and basic medical assessments.",
      features: [
        "Regular Check-ups",
        "Chronic Condition Monitoring",
        "Prescription Support",
        "Medical Assessments",
      ],
      // Note: You'll need to add this image to your assets
      image: doctorVisitImage, // Placeholder - replace with actual image
      color: "#4CAF50", // Green accent for medical
    },
    {
      name: "GERIATRIC COUNSELING",
      icon: "🧠",
      description:
        "Professional counseling support for elderly patients and families to address emotional well-being, memory care guidance, and healthy aging support.",
      features: [
        "Emotional Well-being",
        "Memory Care Guidance",
        "Lifestyle Adjustment",
        "Anxiety Management",
      ],
      // Note: You'll need to add this image to your assets
      image: counselingImage, // Placeholder - replace with actual image
      color: "#2196F3", // Blue accent for counseling
    },
  ];

  return (
    <Box
      ref={servicesRef}
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(135deg, #ffffff 0%, #f4f6f8 100%)",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box textAlign="center" mb={10}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2.4rem", md: "3.4rem" },
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Care Services
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: 720,
              mx: "auto",
              fontSize: "1.05rem",
            }}
          >
            Compassionate, professional home care services tailored to your
            loved one's needs.
          </Typography>
        </Box>

        {/* Services Grid - Updated to show 3 per row on desktop */}
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in timeout={600 + index * 200}>
                <Box
                  sx={{
                    position: "relative",
                    height: 420,
                    borderRadius: 4,
                    overflow: "hidden",
                    cursor: "pointer",
                    boxShadow: theme.shadows[6],
                    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-18px) scale(1.02)",
                      boxShadow: theme.shadows[20],
                    },
                    "&:hover .hoverContent": {
                      transform: "translateY(0)",
                      opacity: 1,
                    },
                    "&:hover img": {
                      transform: "scale(1.15)",
                    },
                  }}
                >
                  {/* Image */}
                  <Box
                    component="img"
                    src={service.image}
                    alt={service.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.8s ease",
                    }}
                  />

                  {/* Gradient Overlay - with accent color for new services */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: service.color
                        ? `linear-gradient(to top, rgba(0,0,0,0.85), ${alpha(
                            service.color,
                            0.3,
                          )} 30%, rgba(0,0,0,0.25))`
                        : "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.25))",
                    }}
                  />

                  {/* Service Title with Icon for new services */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 24,
                      left: 24,
                      right: 24,
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {service.icon && (
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          lineHeight: 1,
                        }}
                      >
                        {service.icon}
                      </Typography>
                    )}
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                      }}
                    >
                      {service.name}
                    </Typography>
                  </Box>

                  {/* Hover Content */}
                  <Box
                    className="hoverContent"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      background: service.color
                        ? `linear-gradient(to top, rgba(0,0,0,0.95), ${alpha(
                            service.color,
                            0.2,
                          )})`
                        : "linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.65))",
                      transform: "translateY(60px)",
                      opacity: 0,
                      transition: "all 0.6s ease",
                      zIndex: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#e0e0e0",
                        mb: 2,
                        fontSize: "0.95rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      {service.features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            background: alpha("#fff", 0.15),
                            color: "#fff",
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            border: service.color
                              ? `1px solid ${alpha(service.color, 0.3)}`
                              : "none",
                          }}
                        >
                          {feature}
                        </Box>
                      ))}
                    </Box>

                    {/* CTA */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        color: service.color
                          ? theme.palette.primary.light
                          : theme.palette.primary.light,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        "& svg": {
                          transition: "transform 0.3s ease",
                        },
                        "&:hover svg": {
                          transform: "translateX(6px)",
                        },
                      }}
                    >
                      Learn More <ArrowForwardIcon fontSize="small" />
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* New Services Notice */}
        <Fade in timeout={1800}>
          <Box
            sx={{
              mt: 8,
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              background: alpha(theme.palette.success.light, 0.1),
              border: `2px dashed ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.success.dark,
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <span>✨</span> New Services Available
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.95rem",
              }}
            >
              We're excited to introduce Doctor Home Visit and Geriatric
              Counseling Services to provide comprehensive care for your loved
              ones.
            </Typography>
          </Box>
        </Fade>

        {/* Bottom CTA */}
        <Fade in timeout={1600}>
          <Box
            sx={{
              mt: 8,
              p: 5,
              textAlign: "center",
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.12,
              )}, ${alpha(theme.palette.secondary.main, 0.12)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Need a Custom Care Plan?
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                maxWidth: 600,
                mx: "auto",
                mb: 3,
              }}
            >
              Contact us today for a personalized care plan designed for your
              loved one.
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              📞 +1 408-320-7170 &nbsp; |📞 +91 70198 54530 &nbsp; | &nbsp; ✉️
              support@athomegericare.com
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ServiceSection;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Container,
//   useTheme,
//   alpha,
//   Fade,
// } from "@mui/material";
// import dayCareImage from "assets/daycare.jpg";
// import nightCareImage from "assets/nightcare.jpg";
// import stayAtHomeImage from "assets/stayathome.jpg";
// import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// const ServiceSection = ({ servicesRef }) => {
//   const theme = useTheme();
//   const [hoveredIndex, setHoveredIndex] = useState(null);

//   const services = [
//     {
//       name: "AT HOME DAY CARE",
//       image: dayCareImage,
//       description:
//         "Professional daytime care services ensuring comfort and safety throughout the day",
//       features: [
//         "8-12 hour shifts",
//         "Medication management",
//         "Daily activities",
//         "Meal preparation",
//       ],
//     },
//     {
//       name: "AT HOME NIGHT CARE",
//       image: nightCareImage,
//       description:
//         "Overnight care and assistance for peaceful nights and restful sleep",
//       features: [
//         "Overnight supervision",
//         "Sleep assistance",
//         "Emergency response",
//         "Comfort care",
//       ],
//     },
//     {
//       name: "STAY AT HOME CARE",
//       image: stayAtHomeImage,
//       description:
//         "24/7 comprehensive care services for round-the-clock support and companionship",
//       features: [
//         "24/7 care",
//         "Personal assistance",
//         "Household help",
//         "Companionship",
//       ],
//     },
//   ];

//   return (
//     <Box
//       ref={servicesRef}
//       sx={{
//         position: "relative",
//         overflow: "hidden",
//         background: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
//         py: { xs: 8, md: 12 },
//       }}
//     >
//       <Container maxWidth="lg">
//         {/* Section Header */}
//         <Box sx={{ textAlign: "center", mb: 8 }}>
//           <Typography
//             variant="h2"
//             sx={{
//               fontSize: { xs: "2.5rem", md: "3.5rem" },
//               fontWeight: 800,
//               background: `linear-gradient(135deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main} 100%)`,
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//               mb: 2,
//               fontFamily: "'Poppins', sans-serif",
//             }}
//           >
//             Our Care Services
//           </Typography>
//           <Typography
//             variant="h6"
//             sx={{
//               color: "text.secondary",
//               maxWidth: "800px",
//               mx: "auto",
//               fontSize: "1.1rem",
//             }}
//           >
//             Comprehensive care solutions tailored to your loved one's needs
//           </Typography>
//         </Box>

//         {/* Services Grid */}
//         <Grid container spacing={4}>
//           {services.map((service, index) => (
//             <Grid item xs={12} md={4} key={index}>
//               <Fade in timeout={500 + index * 200}>
//                 <Box
//                   sx={{
//                     height: "100%",
//                     position: "relative",
//                     borderRadius: theme.shape.borderRadius * 3,
//                     overflow: "hidden",
//                     boxShadow: theme.shadows[4],
//                     transition: "all 0.5s ease",
//                     transform:
//                       hoveredIndex === index
//                         ? "translateY(-16px)"
//                         : "translateY(0)",
//                     "&:hover": {
//                       boxShadow: theme.shadows[16],
//                     },
//                   }}
//                   onMouseEnter={() => setHoveredIndex(index)}
//                   onMouseLeave={() => setHoveredIndex(null)}
//                 >
//                   {/* Image */}
//                   <Box
//                     sx={{
//                       height: "300px",
//                       position: "relative",
//                       overflow: "hidden",
//                     }}
//                   >
//                     <Box
//                       component="img"
//                       src={service.image}
//                       alt={service.name}
//                       sx={{
//                         width: "100%",
//                         height: "100%",
//                         objectFit: "cover",
//                         transition: "transform 0.5s ease",
//                         transform:
//                           hoveredIndex === index ? "scale(1.1)" : "scale(1)",
//                       }}
//                     />

//                     {/* Overlay gradient */}
//                     <Box
//                       sx={{
//                         position: "absolute",
//                         top: 0,
//                         left: 0,
//                         right: 0,
//                         bottom: 0,
//                         background: `linear-gradient(to bottom, transparent 40%, ${alpha(
//                           "#000",
//                           0.7
//                         )} 100%)`,
//                       }}
//                     />

//                     {/* Service name overlay */}
//                     <Box
//                       sx={{
//                         position: "absolute",
//                         bottom: 0,
//                         left: 0,
//                         right: 0,
//                         p: 3,
//                         background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
//                         transform: "skewY(-2deg)",
//                         transformOrigin: "left bottom",
//                       }}
//                     >
//                       <Typography
//                         variant="h5"
//                         sx={{
//                           color: "white",
//                           fontWeight: 700,
//                           transform: "skewY(2deg)",
//                           fontSize: { xs: "1.25rem", md: "1.5rem" },
//                         }}
//                       >
//                         {service.name}
//                       </Typography>
//                     </Box>
//                   </Box>

//                   {/* Content */}
//                   <Box
//                     sx={{
//                       p: 3,
//                       background: "white",
//                       minHeight: "250px",
//                       display: "flex",
//                       flexDirection: "column",
//                     }}
//                   >
//                     {/* Description */}
//                     <Typography
//                       sx={{
//                         color: "text.secondary",
//                         mb: 3,
//                         flex: 1,
//                         fontSize: "1rem",
//                         lineHeight: 1.6,
//                       }}
//                     >
//                       {service.description}
//                     </Typography>

//                     {/* Features */}
//                     <Box>
//                       <Typography
//                         variant="subtitle2"
//                         sx={{
//                           color: "text.primary",
//                           fontWeight: 600,
//                           mb: 2,
//                           fontSize: "0.9rem",
//                         }}
//                       >
//                         Included Services:
//                       </Typography>
//                       <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                         {service.features.map((feature, idx) => (
//                           <Box
//                             key={idx}
//                             sx={{
//                               px: 1.5,
//                               py: 0.5,
//                               background: alpha(
//                                 theme.palette.primary.light,
//                                 0.1
//                               ),
//                               color: theme.palette.primary.main,
//                               borderRadius: 1,
//                               fontSize: "0.8rem",
//                               fontWeight: 500,
//                               transition: "all 0.3s ease",
//                               "&:hover": {
//                                 background: alpha(
//                                   theme.palette.primary.light,
//                                   0.2
//                                 ),
//                                 transform: "translateY(-2px)",
//                               },
//                             }}
//                           >
//                             {feature}
//                           </Box>
//                         ))}
//                       </Box>
//                     </Box>

//                     {/* CTA Button */}
//                     <Box sx={{ mt: 3, textAlign: "center" }}>
//                       <Box
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 1,
//                           color: theme.palette.primary.main,
//                           fontWeight: 600,
//                           fontSize: "0.95rem",
//                           cursor: "pointer",
//                           transition: "all 0.3s ease",
//                           "&:hover": {
//                             gap: 2,
//                             color: theme.palette.primary.dark,
//                           },
//                         }}
//                       >
//                         <span>Learn More</span>
//                         <ArrowForwardIcon fontSize="small" />
//                       </Box>
//                     </Box>
//                   </Box>

//                   {/* Decorative element */}
//                   <Box
//                     sx={{
//                       position: "absolute",
//                       top: 16,
//                       right: 16,
//                       width: "40px",
//                       height: "40px",
//                       background: alpha(theme.palette.primary.main, 0.1),
//                       borderRadius: "50%",
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                       color: theme.palette.primary.main,
//                       fontSize: "1.2rem",
//                     }}
//                   >
//                     {index + 1}
//                   </Box>
//                 </Box>
//               </Fade>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Bottom CTA */}
//         <Fade in timeout={1500}>
//           <Box
//             sx={{
//               mt: 8,
//               p: 4,
//               background: `linear-gradient(135deg, ${alpha(
//                 theme.palette.primary.main,
//                 0.1
//               )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
//               borderRadius: theme.shape.borderRadius * 3,
//               textAlign: "center",
//               border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
//             }}
//           >
//             <Typography
//               variant="h5"
//               sx={{
//                 fontWeight: 700,
//                 color: theme.palette.primary.main,
//                 mb: 2,
//               }}
//             >
//               Need a Custom Care Plan?
//             </Typography>
//             <Typography
//               sx={{
//                 color: "text.secondary",
//                 mb: 3,
//                 maxWidth: "600px",
//                 mx: "auto",
//               }}
//             >
//               Contact us for a personalized care plan tailored to your loved
//               one's specific needs
//             </Typography>
//             <Box
//               sx={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: 2,
//                 p: 2,
//                 background: "white",
//                 borderRadius: theme.shape.borderRadius * 2,
//                 boxShadow: theme.shadows[2],
//               }}
//             >
//               <Typography sx={{ fontWeight: 600 }}>
//                 Call: +1 (800) 123-4567
//               </Typography>
//               <Box
//                 sx={{
//                   width: "1px",
//                   height: "20px",
//                   background: theme.palette.divider,
//                 }}
//               />
//               <Typography sx={{ fontWeight: 600 }}>
//                 Email: care@athomegericare.com
//               </Typography>
//             </Box>
//           </Box>
//         </Fade>
//       </Container>
//     </Box>
//   );
// };

// export default ServiceSection;
// import React from "react";
// import {
//   Box,
//   Card,
//   CardMedia,
//   Grid,
//   Typography,
//   Container,
//   styled,
//   alpha,
//   Button,
// } from "@mui/material";
// import dayCareImage from "assets/daycare.jpg";
// import nightCareImage from "assets/nightcare.jpg";
// import stayAtHomeImage from "assets/stayathome.jpg";
// import { motion } from "framer-motion";
// import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// const ServicesSection = styled(Box)(({ theme }) => ({
//   padding: theme.spacing(10, 0),
//   backgroundColor: "#ffffff",
//   position: "relative",
//   overflow: "hidden",
//   "&::before": {
//     content: '""',
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: "100%",
//     background:
//       "linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)",
//     zIndex: 0,
//   },
// }));

// const ServiceCard = styled(Card)(({ theme }) => ({
//   height: "500px",
//   position: "relative",
//   borderRadius: "25px",
//   overflow: "hidden",
//   boxShadow: "0 20px 50px rgba(0,0,0,0.1)",
//   cursor: "pointer",
//   transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
//   "&:hover": {
//     transform: "translateY(-20px)",
//     boxShadow: "0 40px 80px rgba(102, 126, 234, 0.2)",
//     "& .service-image": {
//       transform: "scale(1.15)",
//     },
//     "& .service-overlay": {
//       height: "60%",
//       background:
//         "linear-gradient(to top, rgba(102, 126, 234, 0.9), rgba(118, 75, 162, 0.9))",
//       "& .service-title": {
//         transform: "translateY(0)",
//         opacity: 1,
//       },
//       "& .service-description": {
//         transform: "translateY(0)",
//         opacity: 1,
//       },
//       "& .service-button": {
//         transform: "translateY(0)",
//         opacity: 1,
//       },
//     },
//   },
//   [theme.breakpoints.down("md")]: {
//     height: "400px",
//     marginBottom: theme.spacing(3),
//   },
// }));

// const ServiceImage = styled(CardMedia)({
//   width: "100%",
//   height: "100%",
//   objectFit: "cover",
//   transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
// });

// const ServiceOverlay = styled(Box)(({ theme }) => ({
//   position: "absolute",
//   bottom: 0,
//   left: 0,
//   width: "100%",
//   height: "35%",
//   padding: theme.spacing(4),
//   background:
//     "linear-gradient(to top, rgba(102, 126, 234, 0.95), rgba(118, 75, 162, 0.8))",
//   display: "flex",
//   flexDirection: "column",
//   justifyContent: "flex-end",
//   transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
//   color: "white",
// }));

// const ServiceTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "1.75rem",
//   fontWeight: 700,
//   marginBottom: theme.spacing(1.5),
//   fontFamily: "'Poppins', sans-serif",
//   transform: "translateY(20px)",
//   opacity: 0,
//   transition: "all 0.4s ease 0.2s",
// }));

// const ServiceDescription = styled(Typography)(({ theme }) => ({
//   fontSize: "0.95rem",
//   fontWeight: 400,
//   marginBottom: theme.spacing(2),
//   opacity: 0,
//   transform: "translateY(20px)",
//   transition: "all 0.4s ease 0.3s",
//   fontFamily: "'Inter', sans-serif",
//   lineHeight: 1.6,
// }));

// const ServiceButton = styled(Button)(({ theme }) => ({
//   backgroundColor: "white",
//   color: "#667eea",
//   padding: theme.spacing(1, 3),
//   borderRadius: "50px",
//   fontWeight: 600,
//   textTransform: "none",
//   alignSelf: "flex-start",
//   opacity: 0,
//   transform: "translateY(20px)",
//   transition: "all 0.4s ease 0.4s",
//   "&:hover": {
//     backgroundColor: alpha("#ffffff", 0.9),
//     transform: "translateY(-2px)",
//   },
// }));

// // Separate component for service card to avoid hook errors
// const ServiceCardComponent = ({ service, index }) => {
//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 50 }}
//       whileInView={{ opacity: 1, y: 0 }}
//       viewport={{ once: true, amount: 0.1 }}
//       transition={{ duration: 0.6, delay: index * 0.1 }}
//     >
//       <ServiceCard>
//         <ServiceImage
//           component="img"
//           alt={service.name}
//           image={service.image}
//           className="service-image"
//         />
//         <ServiceOverlay className="service-overlay">
//           <ServiceTitle className="service-title">{service.name}</ServiceTitle>
//           <ServiceDescription className="service-description">
//             {service.description}
//           </ServiceDescription>
//           <ServiceButton
//             className="service-button"
//             endIcon={<ArrowForwardIcon />}
//             onClick={() => {
//               /* Add navigation or modal */
//             }}
//           >
//             Learn More
//           </ServiceButton>
//         </ServiceOverlay>
//       </ServiceCard>
//     </motion.div>
//   );
// };

// const ServiceSection = ({ servicesRef }) => {
//   const services = [
//     {
//       name: "AT HOME DAY CARE",
//       image: dayCareImage,
//       description:
//         "Professional daytime care services including medication management, meal preparation, companionship, and daily activities assistance.",
//     },
//     {
//       name: "AT HOME NIGHT CARE",
//       image: nightCareImage,
//       description:
//         "Overnight care services with experienced caregivers ensuring safety, comfort, and peace of mind through the night.",
//     },
//     {
//       name: "STAY AT HOME CARE",
//       image: stayAtHomeImage,
//       description:
//         "Comprehensive 24/7 live-in care services providing round-the-clock support and companionship in the comfort of home.",
//     },
//   ];

//   return (
//     <ServicesSection ref={servicesRef}>
//       <Container maxWidth="lg">
//         <motion.div
//           initial={{ opacity: 0, y: 50 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           viewport={{ once: true, amount: 0.1 }}
//           transition={{ duration: 0.8 }}
//         >
//           {/* Section Header */}
//           <Box sx={{ textAlign: "center", mb: 8 }}>
//             <Typography
//               variant="h1"
//               sx={{
//                 fontSize: { xs: "2.5rem", md: "3.5rem" },
//                 fontWeight: 800,
//                 background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//                 WebkitBackgroundClip: "text",
//                 WebkitTextFillColor: "transparent",
//                 fontFamily: "'Poppins', sans-serif",
//                 mb: 2,
//               }}
//             >
//               Our Services
//             </Typography>
//             <Typography
//               variant="h6"
//               sx={{
//                 color: "#7f8c8d",
//                 fontWeight: 400,
//                 maxWidth: "600px",
//                 margin: "0 auto",
//                 fontFamily: "'Inter', sans-serif",
//                 lineHeight: 1.6,
//               }}
//             >
//               Comprehensive care solutions tailored to meet the unique needs of
//               your loved ones
//             </Typography>
//           </Box>

//           {/* Services Grid */}
//           <Grid container spacing={4}>
//             {services.map((service, index) => (
//               <Grid item xs={12} md={4} key={index}>
//                 <ServiceCardComponent service={service} index={index} />
//               </Grid>
//             ))}
//           </Grid>

//           {/* CTA Section */}
//           <motion.div
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             viewport={{ once: true, amount: 0.1 }}
//             transition={{ duration: 0.6, delay: 0.5 }}
//           >
//             <Box
//               sx={{
//                 textAlign: "center",
//                 mt: 8,
//                 p: 4,
//                 borderRadius: "20px",
//                 backgroundColor: alpha("#667eea", 0.05),
//               }}
//             >
//               <Typography
//                 variant="h4"
//                 sx={{
//                   mb: 3,
//                   fontWeight: 700,
//                   color: "#2c3e50",
//                   fontFamily: "'Poppins', sans-serif",
//                 }}
//               >
//                 Ready to Get Started?
//               </Typography>
//               <Typography
//                 sx={{
//                   mb: 4,
//                   color: "#7f8c8d",
//                   maxWidth: "600px",
//                   margin: "0 auto",
//                   fontFamily: "'Inter', sans-serif",
//                 }}
//               >
//                 Contact us today for a free consultation and personalized care
//                 plan
//               </Typography>
//               <Button
//                 variant="contained"
//                 size="large"
//                 sx={{
//                   background:
//                     "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//                   color: "white",
//                   padding: "12px 40px",
//                   borderRadius: "50px",
//                   fontWeight: 600,
//                   textTransform: "none",
//                   fontSize: "1.1rem",
//                   boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
//                   "&:hover": {
//                     transform: "translateY(-3px)",
//                     boxShadow: "0 15px 40px rgba(102, 126, 234, 0.4)",
//                   },
//                   transition: "all 0.3s ease",
//                 }}
//               >
//                 Schedule a Free Consultation
//               </Button>
//             </Box>
//           </motion.div>
//         </motion.div>
//       </Container>
//     </ServicesSection>
//   );
// };

// export default ServiceSection;
